import React, { useState } from "react";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { Input } from "../../../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
  Description,
} from "../../../design/fieldset";
import { useMatterType } from "../../../contexts/MatterTypeContext";
import { Textarea } from "../../../design/textarea";
import { useNavigate } from "react-router-dom";
import { Switch, SwitchField } from "../../../design/switch";
import { PlusIcon } from "@heroicons/react/16/solid";

interface Error {
  message: string;
}

interface Props {
  className?: string;
  children?: React.ReactNode;
}

const CreateMatterTypeButton: React.FC<Props> = ({ className, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { createMatterType } = useMatterType();
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [qualifications, setQualifications] = useState<string>("");
  const [isPrivate, setIsPrivate] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error | null>();
  const navigate = useNavigate();

  const handleSubmit = async () => {
    if (!name) {
      return;
    }
    setIsSaving(true);
    try {
      const matterTypeId = await createMatterType({
        name,
        description,
        qualifications,
        questionTaskStatus: "processing",
        eventTypeTaskStatus: "processing",
        isPrivate,
      });
      navigate(`/matter-type/${matterTypeId}`);
    } catch (error) {
      console.error(error);
      setError(error as Error);
    } finally {
      setIsSaving(false);
    }
  };

  const clearForm = () => {
    setName("");
    setDescription("");
    setIsPrivate(false);
    setError(null);
    setIsOpen(false);
  };

  return (
    <>
      <Button
        className={className}
        type="button"
        onClick={() => setIsOpen(true)}
      >
        {children ?? (
          <>
            <PlusIcon className="w-4 h-4" />
            <span className="hidden sm:block">Create Matter Type</span>
          </>
        )}
      </Button>
      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>Create Matter Type</DialogTitle>
        <DialogBody>
          {error && <ErrorMessage>{error.message}</ErrorMessage>}
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Identifier</Label>
                <Input
                  type="text"
                  placeholder='e.g. "Divorce"'
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Field>
              <Field>
                <Label>Description</Label>
                <Textarea
                  placeholder='e.g. "This is a description of the matter type"'
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Field>
              <Field>
                <Label>Qualifications</Label>
                <Textarea
                  placeholder="The qualifications for this matter type"
                  rows={3}
                  value={qualifications}
                  onChange={(e) => setQualifications(e.target.value)}
                />
              </Field>
              <SwitchField>
                <Label>Is Private?</Label>
                <Description>
                  Clients are not able to select this matter unless they are
                  given a direct link.
                </Description>
                <Switch
                  defaultChecked={isPrivate}
                  onChange={(checked) => setIsPrivate(checked)}
                />
              </SwitchField>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={clearForm}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={handleSubmit}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateMatterTypeButton;
